import { Box, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { FormLabel, MButton, ProgressLoaders } from 'components'
import { type ServiceAddressType } from 'components/layouts/AuthorizedLayout/component/ServiceAddress'
import { MESSAGES } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import type React from 'react'
import { useEffect, useState } from 'react'
import { APIs } from 'services/apis'
import { parseAddress } from 'utils/helpers/address'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { FormType, type InformationFormType } from './ServiceForm'

interface SelectServiceAddressFormProps {
    handleNext: () => void
    handleBack: () => void
    formData: InformationFormType
    formType: string
    setFormData: (data: InformationFormType) => void
}

const SelectServiceAddressForm: React.FC<SelectServiceAddressFormProps> = ({ handleNext, handleBack, formData, setFormData, formType }: SelectServiceAddressFormProps) => {
    const isMobile = useMobileBreakpoints()
    const [selectedAddress, setSelectedAddress] = useState<string>('')
    const { account } = useAuth()
    const { enqueueSnackbar } = useSnackbar()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [serviceAddress, setServiceAddress] = useState<ServiceAddressType[]>([])

    const handleSubmit = async (): Promise<void> => {
        try {
            if (formType === FormType.Transfer) {
                const { address1, address2, city, state, zipCode } = parseAddress(selectedAddress ?? '')
                setFormData({
                    ...formData,
                    svcAddressLine1: address1,
                    svcAddressLine2: address2 ?? '',
                    svcAddressCity: city,
                    svcAddressState: state,
                    svcAddressZipCode: zipCode
                })
            } else {
                setFormData({
                    ...formData,
                    selectedAddress
                })
            }


            handleNext()
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (formType === FormType.Stop && formData?.selectedAddress != null) {
            setSelectedAddress(formData?.selectedAddress)
        } else {
            const selectedAddressConcat = `${formData?.svcAddressLine1}, ${formData?.svcAddressLine2 !== '' ? formData?.svcAddressLine2 : ''} ${formData?.svcAddressCity}, ${formData?.svcAddressState}, ${formData?.svcAddressZipCode}`
            setSelectedAddress(selectedAddressConcat)
        }
    }, [formData, formType])

    useEffect(() => {
        const getServiceAddress = async () => {
            setIsLoading(true)
            try {
                const response = await APIs.getServiceAddress({ accountId: formData.accountId ?? '', lastName: formData.lastName ?? '' })
                if (response?.length > 0) {
                    setServiceAddress(response)
                }
            } catch (error) {
                enqueueSnackbar(MESSAGES.VERIFY_PAY_BILL_ERROR_TEMP, { variant: 'error' })
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }

        if (!account) {
            getServiceAddress()
        } else {
            setServiceAddress(account?.myAccount?.serviceAddresses)
        }
    }, [account])


    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSelectedAddress((event.target as HTMLInputElement).value)
    }

    return (
        <Box mb={serviceAddress.length + 10}>
            {isLoading
                ? <ProgressLoaders height="100%" />
                : <Box sx={{ width: '100%', py: 2, maxHeight: 200, overflow: 'auto', borderTop: `solid 1px ${color.grey[300]}`, borderBottom: `solid 1px ${color.grey[300]}` }}>
                    <FormLabel>ADDRESS</FormLabel>
                    <RadioGroup value={selectedAddress} onChange={handleChange}>
                        {serviceAddress?.map((serviceAddress, index) => <FormControlLabel key={index} value={serviceAddress.serviceAddress} control={<Radio />} label={serviceAddress.serviceAddress} />)}
                    </RadioGroup>
                </Box>}
            <Box display="flex" mb={3} mt={5} width={isMobile ? '100%' : '50%'} flexDirection={isMobile ? 'column' : 'row'} gap={2}>
                <MButton fullWidth variant='outlined' texttransform='none' rounded='true' onClick={() => { handleBack() }}>
                    Back
                </MButton>
                <MButton fullWidth variant='contained' type='submit' texttransform='none' rounded='true' disabled={selectedAddress === ''} onClick={handleSubmit}>
                    Confirm Address
                </MButton>
            </Box>
        </Box>
    )
}

export default SelectServiceAddressForm
